import { AI_AUTOPILOT_USER_ID } from "@redotech/ai-autopilot-constants/constant";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoClient } from "@redotech/redo-api-client";
import { renderMessageContent } from "@redotech/redo-chat-widget/utils/message-render-utils";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_MERCHANT_SERVER_URL } from "@redotech/redo-merchant-app-common/config";
import { listen } from "@redotech/redo-merchant-app-common/events/utils";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ContentType,
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { ConversationMessageDbParser } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import { ConversationSchemaDbParser } from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { parseItem } from "@redotech/redo-model/parse-item";
import { RedoClientContext } from "@redotech/redo-web/client";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { formatDate } from "@redotech/redo-web/time";
import { stableComparator } from "@redotech/util/array";
import classNames from "classnames";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AIFeedback } from "./ai-feedback";
import { getProfilePictures } from "./client/conversations";
import * as conversationMessagesCss from "./conversation-messages.module.css";
import { Message } from "./message/message";
import { PrivateReplyModal } from "./private-reply-modal";
import { VoiceTranscriptMessage } from "./voice-transcript-message";

const SCROLL_OFFSET = 36;

const getConversationStream = (
  client: RedoClient,
  { conversationId, signal }: { conversationId: string; signal: AbortSignal },
) => {
  const url = `${REDO_MERCHANT_SERVER_URL}/conversations/${conversationId}/live`;
  return fetch(url, { signal, headers: client.authorization() });
};

export const ConversationMessages = memo(function ConversationMessages({
  fullConversation,
  fullConversationLoading,
  conversation,
  typing,
  setErrorMessage,
  setShowErrorMessage,
  setActiveConversation,
  showFullCommentThread,
}: {
  fullConversation: ExpandedConversation | undefined;
  fullConversationLoading: boolean;
  conversation: MinimalConversation;
  typing: Record<string, Date>;
  setErrorMessage: (message: string) => void;
  setShowErrorMessage: (show: boolean) => void;
  setActiveConversation: (conversation: MinimalConversation) => void;
  showFullCommentThread: boolean;
}) {
  const messagesCardRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  const [retryCount, setRetryCount] = useState(0);
  const [privateReplyModalOpen, setPrivateReplyModalOpen] =
    useState<boolean>(false);
  const [privateReplyMessage, setPrivateReplyMessage] =
    useState<ExpandedConversationMessage>();
  const client = useRequiredContext(RedoClientContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [transcriptionMessage, setTranscriptionMessage] =
    useState<ExpandedConversationMessage>();

  const handleDrawerState = (message: ExpandedConversationMessage) => {
    if (transcriptionMessage && transcriptionMessage._id === message._id) {
      setTranscriptionMessage(undefined);
      setDrawerOpen(false);
    } else {
      setTranscriptionMessage(message);
      setDrawerOpen(true);
    }
  };

  const profilePicturesLoad = useLoad(async (signal) => {
    const profilePictures = await getProfilePictures(merchantClient, {
      conversationId: conversation._id,
      signal,
    });

    return profilePictures;
  }, []);

  const messages: ExpandedConversationMessage[] = useMemo(() => {
    if (!fullConversation || fullConversationLoading) {
      return [];
    }
    return fullConversation.messages;
  }, [fullConversation, fullConversationLoading]);

  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      try {
        for await (const updatedConversation of listen({
          query: async () => {
            return await getConversationStream(client, {
              conversationId: conversation._id,
              signal: abortController.signal,
            });
          },
          loopCondition: !!conversation._id,
          setErrorMessage,
          setShowErrorMessage,
        })) {
          setActiveConversation(
            updatedConversation as unknown as ExpandedConversation,
          );
        }
      } catch (e) {
        // Handle AbortError gracefully - don't retry or throw
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        // Wait 5 seconds before trying again for other errors
        setTimeout(() => {
          // Continue polling for changes
          setRetryCount(retryCount + 1);
        }, 5000);
        if (abortController.signal.aborted) {
          return;
        }
        throw e;
      }
    })();
    return () =>
      abortController.abort("Conversation changed or component unmounted");
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation._id, retryCount]);

  let messagesToShow: ExpandedConversationMessage[] = messages || [];
  if (
    (!!fullConversation?.instagramCommentThread ||
      !!fullConversation?.facebookCommentThread) &&
    !showFullCommentThread
  ) {
    messagesToShow = messagesToShow.filter(
      (message) =>
        message.type === "merchant" ||
        message.type === "system" ||
        message.customer?._id === conversation.customer.customer,
    );
  }

  // Filter messages by type and content
  messagesToShow = messagesToShow.filter(
    (message) =>
      // Don't show drafts
      !message.draftInfo?.isDraft &&
      (conversation.platform !== ConversationPlatform.REDO_CHAT ||
        message.type === "customer" ||
        message.type === "merchant" ||
        message.type === "system"),
  );

  messagesToShow.sort(
    stableComparator(
      (m) => new Date(m.sentAt).getTime(),
      (m) => m._id,
    ),
  );

  const team = useRequiredContext(TeamContext);

  const productFetcher = useCallback(
    async (productIds: string[], signal?: AbortSignal) => {
      if (!merchantClient) return [];
      return await (merchantClient as any).lookupProductsByIds(
        { productIds },
        { signal },
      );
    },
    [merchantClient],
  );

  useEffect(() => {
    if (lastMessageRef.current && messagesCardRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: "instant",
        block: "start",
      });

      setTimeout(() => {
        if (messagesCardRef.current) {
          messagesCardRef.current.scrollTop += SCROLL_OFFSET;
        }
      }, 100);
    }
  }, [messagesToShow.length]);

  if (fullConversationLoading) {
    return <LoadingRedoAnimation />;
  }

  return (
    <>
      <div className={conversationMessagesCss.messagesCard}>
        <div
          className={classNames(
            conversationMessagesCss.messagesContainerWrapper,
            {
              [conversationMessagesCss.withPadding]:
                team.settings?.support?.ai?.enabled,
            },
          )}
          ref={messagesCardRef}
        >
          <div className={conversationMessagesCss.messagesContainer}>
            {messagesToShow.map((message, index) => (
              <Fragment key={message._id}>
                {fullConversation && (
                  <>
                    {message.type === "call" ? (
                      <VoiceTranscriptMessage
                        conversation={fullConversation}
                        handleDrawerState={handleDrawerState}
                        message={message}
                        open={drawerOpen}
                        transcriptionMessage={transcriptionMessage}
                      />
                    ) : (
                      <>
                        {index === 0 && (
                          <div
                            className={conversationMessagesCss.headerDivider}
                          >
                            <div className={conversationMessagesCss.divider} />
                            <div
                              className={conversationMessagesCss.createdDate}
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                textColor="tertiary"
                              >
                                {formatDate(
                                  Temporal.Instant.from(conversation.createdAt),
                                )}
                              </Text>
                            </div>
                            <div className={conversationMessagesCss.divider} />
                          </div>
                        )}
                        <Flex align="flex-end" dir="column">
                          {message.contentType &&
                          message.contentType !== ContentType.TEXT ? (
                            // because the merchant-app renders text messages differently
                            // than the chat widget, filter out text messages from the
                            // chat widget specific messages and render them in the merchant app way.
                            renderMessageContent({
                              conversation: parseItem(
                                fullConversation,
                                ConversationSchemaDbParser,
                              ),
                              message: parseItem(
                                message,
                                ConversationMessageDbParser,
                              ),
                              index,
                              filteredMessages: messagesToShow.map((m) =>
                                parseItem(m, ConversationMessageDbParser),
                              ),
                              readOnly: true,
                              storeUrl: team.storeUrl,
                              storefrontAccessToken:
                                team.storefrontAccessToken ?? undefined,
                              productFetcher,
                            })
                          ) : (
                            <Message
                              conversation={fullConversation}
                              index={index}
                              message={message}
                              profilePictures={profilePicturesLoad.value}
                              setActiveConversation={setActiveConversation}
                              setPrivateReplyMessage={setPrivateReplyMessage}
                              setPrivateReplyModalOpen={
                                setPrivateReplyModalOpen
                              }
                            />
                          )}
                          {message.user?._id === AI_AUTOPILOT_USER_ID && (
                            <AIFeedback
                              conversationId={conversation._id}
                              format="chat-bubble"
                              message={message}
                            />
                          )}
                        </Flex>
                        {index === messagesToShow.length - 1 && (
                          <div id="lastElement" ref={lastMessageRef} />
                        )}
                      </>
                    )}
                  </>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      {privateReplyMessage && (
        <PrivateReplyModal
          conversation={conversation}
          message={privateReplyMessage}
          onClose={() => setPrivateReplyModalOpen(false)}
          open={privateReplyModalOpen}
          setActiveConversation={setActiveConversation}
        />
      )}
    </>
  );
});
